import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import apiFetch from "../../../../shared/services/apiClient";
import { useCxc } from "../hooks/useCxc";
import { useTipoCambio } from "../../services/useTipoCambio";
import { usePendingReceipts } from "../hooks/useReceipt";
import { ReceiptDetails } from "../components/Add/ReceiptDetails";
import { CxcDetails } from "../components/Add/CxcDetails";
import { showRestanteError } from "../components/Add/Alerts";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";
import styles from "../styles/addReceipt.module.css";

// Redondear a 2 decimales para evitar 400 por precisión flotante de JS
const r2 = (n) => parseFloat(Number(n).toFixed(2));

export default function ApplyReceipt() {
  const { reciboId } = useParams();
  const navigate = useNavigate();

  const [recibo, setRecibo] = useState(null);
  const [loadingR, setLoadingR] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);
  const [count, setCount] = useState(0);

  // Refresca pendientes después de cada guardado
  const [pendingKey, setPendingKey] = useState(0);
  const { pending } = usePendingReceipts(pendingKey);

  // ── Cargar recibo ─────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingR(true);
    setRecibo(null);
    submittingRef.current = false;
    setSubmitting(false);
    apiFetch(`/api/finance/recibo/${reciboId}/`)
      .then((r) => r.json())
      .then((data) => {
        setRecibo(data);
        setLoadingR(false);
      })
      .catch(() => setLoadingR(false));
  }, [reciboId]);

  // Para depósitos sin identificar, filial se selecciona manualmente
  const [depositoFilialId, setDepositoFilialId] = useState(0);
  const filialId = recibo?.es_deposito_sin_identificar
    ? depositoFilialId
    : (recibo?.filial?.id ?? recibo?.filial ?? 0);

  // Usar saldo sin aplicar (monto pendiente) en vez del monto total
  const _mSA = recibo && recibo.saldo_sin_aplicar != null
    ? recibo.saldo_sin_aplicar : (recibo ? recibo.monto : 0);
  const montoSinAplicar = r2(_mSA);

  // Moneda y TC del recibo — necesarios para cálculos multimoneda de useCxc.
  // Usar cuenta_bancaria.moneda como fuente primaria (el serializador anida el objeto
  // completo), ya que moneda_pago puede estar vacío en recibos importados.
  const bancoMonedaRecibo = (
    recibo?.cuenta_bancaria?.moneda ||
    recibo?.moneda_pago ||
    'CRC'
  ).toUpperCase();
  const { tipoCambio: tcBCCR } = useTipoCambio();
  // Si el recibo se guardó con TC=1 (antes de la corrección), usar el TC actual de BCCR
  const storedTC = Number(recibo?.tipo_cambio || 0);
  const TC_compraRecibo = storedTC > 1
    ? storedTC
    : Number(tcBCCR?.tipo_compra || 1) || 1;

  const cxc = useCxc(montoSinAplicar, filialId, setCount, bancoMonedaRecibo, TC_compraRecibo);
  // Stable noop — must be before any conditional return (Rules of Hooks)
  const noop = useCallback(() => {}, []);

  // ── Guardar detalles ──────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitError("");
    // Para depósitos sin identificar, validar que se seleccionó una filial
    if (recibo?.es_deposito_sin_identificar && !depositoFilialId) {
      setSubmitError('Seleccioná una filial antes de aplicar el depósito.');
      return;
    }
    if (cxc.restante !== 0) {
      showRestanteError().fire();
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      const aplicadas = cxc.facturasData.filter(
        (f) =>
          f.checked &&
          f.abono !== 0 &&
          f.factura != null &&
          f.factura.id_factura != null,
      );

      if (aplicadas.length === 0) {
        showRestanteError().fire();
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }

      for (const f of aplicadas) {
        const res = await apiFetch("/api/finance/reciboDetalle/", {
          method: "POST",
          body: JSON.stringify({
            recibo: Number(reciboId),
            factura: f.factura.id_factura,
            abono: r2(f.abono), // ✅ siempre 2 decimales
            tratar_como_diferencial: f.tratar_como_diferencial ?? false,
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const body = JSON.stringify(err);
          const isDup = res.status === 400 && body.includes("unique");
          if (isDup) continue;
          console.error("DetalleRecibo 400:", err);
          throw new Error(
            err.abono?.[0] ?? err.detail ?? err.non_field_errors?.[0] ?? body,
          );
        }
      }

      // Refrescar y navegar al siguiente pendiente (si existe) o a la lista
      setPendingKey((k) => k + 1);
      const siguientes = pending.filter((p) => p.id !== Number(reciboId));
      navigate(
        siguientes.length > 0
          ? `/finanzas/recibos/${siguientes[0].id}/aplicar`
          : "/finanzas/recibos",
      );
    } catch (err) {
      console.error("Error:", err.message);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  if (loadingR) {
    return (
      <p style={{ padding: "20px", color: "var(--text-secondary)" }}>Cargando recibo...</p>
    );
  }
  if (!recibo) {
    return (
      <p style={{ padding: "20px", color: "#f87171" }}>Recibo no encontrado.</p>
    );
  }

  const esDeposito = recibo?.es_deposito_sin_identificar ?? false;
  const siguientes = pending.filter((p) => p.id !== Number(reciboId));

  const receiptData = {
    monto: r2(recibo.monto),
    cuenta_bancaria: recibo.cuenta_bancaria?.id ?? recibo.cuenta_bancaria,
    metodo_pago: recibo.metodo_pago,
    documento: recibo.documento,
    fecha_pago: recibo.fecha_pago,
  };


  return (
    <>
      
              <span className={styles.inactive}>
                <Link to="/">
                  <span className={styles.inactive}>Inicio /</span>
                </Link>
                <Link to="/finanzas/cxc">
                  <span className={styles.inactive}> Cuentas por Cobrar /</span>
                </Link>
                <Link to="/finanzas/recibos">
                  <span className={styles.inactive}> Recibos /</span>
                </Link>
                <span className={styles.active}> Aplicar {recibo.numero_recibo}</span>
              </span>

      <h2>Aplicar Recibo — {recibo.numero_recibo}</h2>
      {esDeposito && (
        <div style={{ background:"rgba(249,115,22,0.08)", border:"1px solid rgba(249,115,22,0.3)",
          borderRadius:8, padding:"10px 16px", marginBottom:12, fontSize:"0.85rem", color:"#fdba74" }}>
          <i className="fa-solid fa-vault" style={{marginRight:6}}/>
          <strong>Depósito sin identificar.</strong> Seleccioná la filial a la que pertenece este depósito.
          Se generará la contrapartida contable automáticamente.
        </div>
      )}

      {/* ── Banner info del recibo actual ───────────────────────────────── */}
      <div
        style={{
          background: "rgba(249,115,22,0.1)",
          border: "1px solid rgba(249,115,22,0.35)",
          borderLeft: "4px solid #f97316",
          borderRadius: "6px",
          padding: "10px 16px",
          marginBottom: "12px",
          fontSize: "0.87rem",
          color: "#fdba74",
        }}
      >
        <strong>⚠️ Recibo pendiente de aplicar.</strong> Los datos ya están
        guardados y no se pueden modificar. Selecciona las facturas a las que
        deseas aplicarlo y confirma.
        {siguientes.length > 0 && (
          <span style={{ marginLeft: 8, color: "#fb923c" }}>
            · Quedan <strong>{siguientes.length}</strong> recibo(s) más por
            aplicar.
          </span>
        )}
      </div>

      {/* ── Tabla paginada de todos los pendientes (incluye el actual) ─── */}
      <PendingReceiptsTable
        pendientes={pending}
        currentId={Number(reciboId)} // ← se resalta el recibo actual
        pageSize={5}
        title={`Cola de pendientes — ${pending.length} recibo(s) sin aplicar`}
      />

      {/* ── Formulario idéntico a AddReceipt ──────────────────────────── */}
      {(
      <div className={styles["receipt-container"]}>
        <ReceiptDetails
          receiptData={receiptData}
          updateField={noop}
          selectedFilial={filialId}
          setSelectedFilial={esDeposito ? setDepositoFilialId : noop}
          onDistribute={cxc.distribute}
          displayValue={
            montoSinAplicar > 0
              ? bancoMonedaRecibo === "USD"
                ? Number(montoSinAplicar).toFixed(2)
                : formatCurrency(montoSinAplicar)
              : ""
          }
          setDisplayValue={() => {}}
          readOnly={true}
          filialEditable={esDeposito}
        />

        <CxcDetails
          {...cxc}
          onDistribute={cxc.distribute}
          toggleDiferencial={cxc.toggleDiferencial}
          receiptMonto={montoSinAplicar}
          count={count}
        />

        <div className={styles["receipt-footer"]}>
          <div className={styles.actions}>
            <Link
              to="/finanzas/recibos"
              className={`${styles.btn} ${styles["btn-clean-form"]}`}
            >
              <i className="fa-solid fa-arrow-left" /> Volver sin guardar
            </Link>
            <button
              className={`${styles.btn} ${styles["btn-save"]}`}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                  &nbsp;Guardando...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save" />{" "}
                  {siguientes.length > 0
                    ? `Guardar y siguiente (${siguientes.length})`
                    : "Guardar Aplicación"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      )}
    </>
  );
}

